import { useEffect, useState } from 'react';
import { AgenciesService } from '@services/requests';
import type { SeatUsageHistory } from '@services/requests/housien-services/AgenciesService.subscription';

export interface UseUsageHistoryResult {
  data: SeatUsageHistory | null;
  isLoading: boolean;
}

/**
 * Fetches the seat's burn-rate projection and prior-cycle history.
 *
 * A failure resolves to null rather than to an error state, the same contract
 * as useSeatUsage: the projection sharpens the plan tab's argument but the
 * screen is complete without it, so an endpoint having a bad minute must not
 * cost the user their price, their invoices or their cancel link.
 *
 * @param agencyId - The agency identifier.
 * @returns The history, or null while loading or after a failure.
 */
export function useUsageHistory(agencyId: string): UseUsageHistoryResult {
  const [data, setData] = useState<SeatUsageHistory | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!agencyId) return undefined;
    setIsLoading(true);

    AgenciesService.getAgencySeatUsageHistory(agencyId)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [agencyId]);

  return { data, isLoading };
}
